import type { WorkplaceOccupant } from '../services/floorplan.service';
import type { WorkplaceOccupancyMap } from '../hooks/workplace-occupants.hooks';

/**
 * Which desks on the plan get a company medic's mark.
 *
 * <p>
 * Straightforward for a desk somebody booked for themselves or checked into: that is where they
 * are. A group reservation is the hard case. It holds a block of desks for its members and records
 * nothing about who takes which seat, so the server reports every member against every desk in the
 * block - and marking all of them put a medic's mark on the whole pool, which read as "there are
 * eight first aiders here" when there was one.
 * </p>
 *
 * <p>
 * So a block is marked once per medic in it: two medics in a block of eight desks mark two desks.
 * The count is true. <b>Which</b> desks is not knowable and is not claimed to be - the pairing is by
 * sorted name against sorted desk id, chosen only so that it is stable between requests rather than
 * jumping around the block on every refresh. The card on a marked desk lists the whole group,
 * which is what keeps the mark honest to anybody who hovers it.
 * </p>
 *
 * <p>
 * The same rule as the Flutter app's `MedicDeskResolver`, deliberately. The two clients draw the
 * same plan from the same endpoint, and an administrator who sees one desk marked here and three
 * on a colleague's phone has been told two different things about who is in the building.
 * </p>
 *
 * <p>
 * Resolved one day at a time and then combined, because that is the question the rule answers -
 * one half-day's occupants, exactly as the app asks it - and this screen books a range at once. A
 * desk is marked when it is a medic's on any of the days being booked, which is what the
 * administrator choosing a seat for that range needs to know about it.
 * </p>
 */

const displayName = (occupant: WorkplaceOccupant): string =>
  [occupant.firstName, occupant.lastName].filter(Boolean).join(' ').trim();

/**
 * One person, however many desks of a block they are listed against.
 *
 * Email where there is one and name otherwise. This screen is sent addresses, but falling back to
 * the name keeps a withheld address from collapsing every member of a group into one person.
 */
const identityOf = (occupant: WorkplaceOccupant): string => {
  if (occupant.email != null && occupant.email !== '') return occupant.email;
  return displayName(occupant);
};

const byNameThenIdentity = (left: WorkplaceOccupant, right: WorkplaceOccupant): number =>
  displayName(left).localeCompare(displayName(right))
  || identityOf(left).localeCompare(identityOf(right));

/** One day's desks, as the server reported them for that day. */
type DayOfDesks = ReadonlyArray<{ readonly workplaceId: string; readonly occupants: readonly WorkplaceOccupant[] }>;

const medicDesksOnDay = (desks: DayOfDesks): Set<string> => {
  const marked = new Set<string>();
  const sortedDesks = [...desks].sort((left, right) => left.workplaceId.localeCompare(right.workplaceId));

  // Known desks: somebody's own booking of it, or their own scan at it.
  sortedDesks.forEach((desk) => {
    if (desk.occupants.some((occupant) => occupant.isCompanyMedic === true && occupant.groupReservationId == null)) {
      marked.add(desk.workplaceId);
    }
  });

  // Blocks. A desk is in a block if any occupant names that reservation, not only if all of them
  // do - a walk-up check-in on a desk the group holds is on it too, on its own terms.
  const blocks = new Map<string, string[]>();
  sortedDesks.forEach((desk) => {
    new Set(desk.occupants.map((occupant) => occupant.groupReservationId).filter((id): id is string => id != null))
      .forEach((reservationId) => {
        blocks.set(reservationId, [...(blocks.get(reservationId) ?? []), desk.workplaceId]);
      });
  });

  blocks.forEach((blockDeskIds, reservationId) => {
    const medics = new Map<string, WorkplaceOccupant>();
    sortedDesks
      .filter((desk) => blockDeskIds.includes(desk.workplaceId))
      .forEach((desk) => desk.occupants.forEach((occupant) => {
        if (occupant.isCompanyMedic === true && occupant.groupReservationId === reservationId) {
          const identity = identityOf(occupant);
          if (!medics.has(identity)) medics.set(identity, occupant);
        }
      }));

    // A block holds a desk per member, so there cannot be more medics than desks - but a
    // half-written reservation should give a smaller picture, not an index past the end.
    const marks = Math.min(medics.size, blockDeskIds.length);
    Array.from(medics.values()).sort(byNameThenIdentity).slice(0, marks).forEach((unused, index) => {
      marked.add(blockDeskIds[index]);
    });
  });

  return marked;
};

/** Every desk to mark, across every day the plan is showing. */
const medicWorkplaceIds = (occupancy: WorkplaceOccupancyMap | undefined): Set<string> => {
  if (occupancy == null || occupancy.size === 0) return new Set();

  // Regrouped by day: the map arrives keyed by desk, with each desk carrying its own days.
  const days = new Map<string, Array<{ workplaceId: string; occupants: readonly WorkplaceOccupant[] }>>();
  occupancy.forEach((desk) => {
    desk.dates.forEach((day) => {
      days.set(day.date, [...(days.get(day.date) ?? []), { workplaceId: desk.workplaceId, occupants: day.occupants }]);
    });
  });

  const marked = new Set<string>();
  days.forEach((desks) => medicDesksOnDay(desks).forEach((workplaceId) => marked.add(workplaceId)));
  return marked;
};

export default medicWorkplaceIds;
